<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { deploysApi } from '$lib/api/deploys';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { Project } from '$lib/types';

  const { projects, defaultProjectId, onClose }: { projects: Project[]; defaultProjectId?: number; onClose: () => void } =
    $props();

  const queryClient = useQueryClient();

  let projectId = $state(defaultProjectId ?? projects[0]?.id);
  let target = $state('production');
  let by = $state('');
  let artifact = $state('');
  let formError = $state('');

  const mutation = createMutation(() => ({
    mutationFn: () =>
      deploysApi.create({
        project_id: projectId!,
        target,
        artifact: artifact.trim() || undefined,
        by: by.trim() || undefined
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deploys'] });
      onClose();
      toast.success('Deploy recorded');
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!projectId) {
      formError = 'Select a project';
      return;
    }
    formError = '';
    mutation.mutate();
  }
</script>

<Modal title="Record deploy" {onClose}>
  {#snippet footer()}
    <span class="spacer"></span>
    <button type="button" class="btn btn-secondary" onclick={onClose}>Cancel</button>
    <button type="submit" form="deploy-form" class="btn btn-primary" disabled={mutation.isPending}>Record deploy</button>
  {/snippet}

  <form id="deploy-form" onsubmit={submit}>
    <div class="field">
      <label class="label" for="dp-project">Project</label>
      <select id="dp-project" class="select" bind:value={projectId}>
        {#each projects as p (p.id)}
          <option value={p.id}>{p.name}</option>
        {/each}
      </select>
    </div>
    <div class="field">
      <label class="label" for="dp-target">Target</label>
      <select id="dp-target" class="select" bind:value={target}>
        {#each ['production', 'staging', 'OTA stable', 'OTA beta'] as t (t)}
          <option value={t}>{t}</option>
        {/each}
      </select>
    </div>
    <div class="field">
      <label class="label" for="dp-by">Triggered by</label>
      <input id="dp-by" class="input" placeholder="Name or CI pipeline" bind:value={by} />
    </div>
    <div class="field">
      <label class="label" for="dp-artifact">Artifact link</label>
      <input id="dp-artifact" class="input" placeholder="artifacts.toyama.io/repo/sha.bin  (optional)" bind:value={artifact} />
    </div>
    <div style="font-size:12px;color:var(--t3)">A short commit SHA and current timestamp are generated automatically.</div>
    {#if formError}<p style="color:var(--danger);font-size:13px;margin-top:10px">{formError}</p>{/if}
  </form>
</Modal>
